import { DeleteOutlined, FileImageOutlined } from '@ant-design/icons'
import { Button, Card, Form, Input, Select, Typography } from 'antd'
import { FormInstance } from 'antd/es/form'
import { FormListFieldData } from 'antd/es/form/FormList'
import { ImageUpload } from 'pages/reporting/shared/utils/ImageUpload'
import React from 'react'
import styled from 'styled-components'

const { Option } = Select
const { Text } = Typography

const ImageCard = styled(Card)`
  margin-bottom: 1rem;
  border-radius: 0.5rem;
  overflow: hidden;

  .ant-card-head {
    background: ${(props) => props.theme.forecast.cardGroupBackground};
    border-bottom: 1px solid
      ${(props) => props.theme.forecast.cardDividerBackground};
    padding: 0.75rem 1rem;
    min-height: auto;
  }

  .ant-card-head-title {
    padding: 0;
  }

  .ant-card-extra {
    padding: 0;
  }

  .ant-card-body {
    padding: 1.25rem;
  }
`

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const CardHeaderIcon = styled(FileImageOutlined)`
  font-size: 1rem;
  color: ${(props) => props.theme.forecast.primary};
`

const ImageNumber = styled(Text)`
  font-weight: 600;
  font-size: 0.875rem;
`

const TypeSelectWrapper = styled.div`
  .ant-select {
    min-width: 7.5rem;
  }
`

interface ClientImageFieldProps {
  field: FormListFieldData
  index: number
  metadataEntryId: string
  onRemove: () => void
  form: FormInstance // Add form instance to props
}

const imageTypes = [
  { label: 'Drawing', value: 'drawing' },
  { label: 'Photo', value: 'photo' },
  { label: 'Diagram', value: 'diagram' },
  { label: 'Other', value: 'other' },
] as const

export const ClientImageField: React.FC<ClientImageFieldProps> = ({
  field,
  index,
  metadataEntryId,
  onRemove,
  form,
}) => {
  const imagePath = Form.useWatch(['images', field.name, 'imagePath'], form)
  const imageCaption = Form.useWatch(
    ['images', field.name, 'imageCaption'],
    form
  )
  const imageId = Form.useWatch(['images', field.name, 'id'], form)

  const handleImageUpload = async (
    path: string,
    _: string,
    caption?: string
  ) => {
    form.setFields([
      {
        name: ['images', field.name, 'imagePath'],
        value: path,
      },
      {
        name: ['images', field.name, 'imageCaption'],
        value: caption,
      },
    ])
  }

  return (
    <ImageCard
      size='small'
      title={
        <CardHeader>
          <CardHeaderIcon />
          <ImageNumber>Image {index + 1}</ImageNumber>
          <TypeSelectWrapper>
            <Form.Item
              {...field}
              name={[field.name, 'type']}
              rules={[{ required: true, message: 'Please select image type' }]}
              style={{ margin: 0 }}>
              <Select placeholder='Select type'>
                {imageTypes.map((type) => (
                  <Option key={type.value} value={type.value}>
                    {type.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </TypeSelectWrapper>
        </CardHeader>
      }
      extra={
        <Button
          type='text'
          danger
          icon={<DeleteOutlined />}
          onClick={onRemove}
          aria-label={`Remove image ${index + 1}`}
        />
      }>
      {/* Hidden form fields for image data */}
      <Form.Item {...field} name={[field.name, 'id']} hidden>
        <Input id={`client-image-id-${field.name}`} />
      </Form.Item>

      <Form.Item
        {...field}
        name={[field.name, 'imagePath']}
        rules={[{ required: true, message: 'Please upload an image' }]}
        style={{ marginBottom: 0 }}>
        <ImageUpload
          metadataEntryId={metadataEntryId}
          imageData={{
            imagePath,
            imageCaption,
          }}
          onUpload={handleImageUpload}
          fileNamePrefix={imageId}
          subpath='client-images'
          buttonText='Upload Image'
          maxSizeInMB={10}
          acceptedFileTypes={['.png', '.jpg', '.jpeg']}
          imageKey={`image-${field.name}`}
          allowCaption={true}
        />
      </Form.Item>

      <Form.Item {...field} name={[field.name, 'imageCaption']} hidden>
        <Input id={`client-image-caption-${field.name}`} />
      </Form.Item>
    </ImageCard>
  )
}
